import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, Trash2, Plus, CheckCircle2, ArrowLeft, ArrowRight } from "lucide-react";

const STEPS = ["Inputs", "Generate", "Review & Save"];

export default function IEPBuilder({ student, existingIEP, onSaved }) {
  const [step, setStep] = useState(0);
  const [inputs, setInputs] = useState({
    strengths: "",
    needs: "",
    parent_concerns: "",
    assessment_summary: "",
    focus_areas: ["Reading", "Math"],
  });
  const [draft, setDraft] = useState(existingIEP || null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    if (!inputs.strengths || !inputs.needs) {
      toast.error("Please fill in strengths and needs first");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/iep/generate", {
        student_id: student.id,
        ...inputs,
      });
      if (r.data.parsed) {
        setDraft(r.data.parsed);
        setStep(2);
        toast.success("Draft generated. Review and adjust before saving.");
      } else {
        toast.error("AI returned an unexpected format. Try again or refine your inputs.");
      }
    } catch (e) {
      toast.error("Could not generate IEP. Check your inputs and try again.");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await api.post("/iep/save", {
        student_id: student.id,
        present_levels: draft.present_levels || "",
        goals: draft.goals || [],
        accommodations: draft.accommodations || [],
        modifications: draft.modifications || [],
        services: draft.services || [],
        notes: "",
      });
      toast.success("IEP saved. Goals and supports updated.");
      onSaved && onSaved();
    } catch (e) {
      toast.error("Could not save IEP");
    } finally {
      setSaving(false);
    }
  };

  const toggleFocus = (area) => {
    setInputs((prev) => {
      const has = prev.focus_areas.includes(area);
      return {
        ...prev,
        focus_areas: has ? prev.focus_areas.filter((a) => a !== area) : [...prev.focus_areas, area],
      };
    });
  };

  const updateDraftField = (field, value) => setDraft({ ...draft, [field]: value });
  const updateList = (field, idx, value) => {
    const list = [...(draft[field] || [])];
    list[idx] = value;
    setDraft({ ...draft, [field]: list });
  };
  const addToList = (field) => setDraft({ ...draft, [field]: [...(draft[field] || []), ""] });
  const removeFromList = (field, idx) => {
    const list = [...(draft[field] || [])];
    list.splice(idx, 1);
    setDraft({ ...draft, [field]: list });
  };
  const updateGoal = (idx, key, value) => {
    const goals = [...(draft.goals || [])];
    goals[idx] = { ...goals[idx], [key]: value };
    setDraft({ ...draft, goals });
  };
  const addGoal = () => setDraft({ ...draft, goals: [...(draft.goals || []), { area: "", description: "", target_criteria: "", baseline: "" }] });
  const removeGoal = (idx) => {
    const goals = [...(draft.goals || [])];
    goals.splice(idx, 1);
    setDraft({ ...draft, goals });
  };

  return (
    <div className="space-y-6" data-testid="iep-builder">
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${i <= step ? "bg-[#6E5AA0] text-white" : "bg-[#F0EBF6] text-[#6B726F]"}`}>
              {i + 1}
            </div>
            <span className={`text-sm ${i === step ? "ink font-medium" : "ink-muted"}`}>{s}</span>
            {i < STEPS.length - 1 && <div className="w-8 h-px bg-[#E8E9E6] mx-1" />}
          </div>
        ))}
      </div>

      {step === 0 && (
        <Card className="p-6 sm:p-8 border border-[#E8E9E6] bg-white">
          <h3 className="font-heading text-2xl font-medium ink">Tell us about {student.first_name}</h3>
          <p className="ink-muted text-sm mt-1">Share the context you'd put in a draft. AI will turn it into a structured IEP you can edit.</p>

          <div className="mt-6 grid gap-5">
            <div className="space-y-2">
              <Label>Strengths</Label>
              <Textarea rows={3} value={inputs.strengths} onChange={(e) => setInputs({ ...inputs, strengths: e.target.value })} placeholder="What does this student do well? Interests, supports that help, observed skills…" data-testid="iep-strengths" />
            </div>
            <div className="space-y-2">
              <Label>Needs</Label>
              <Textarea rows={3} value={inputs.needs} onChange={(e) => setInputs({ ...inputs, needs: e.target.value })} placeholder="Areas where the student needs growth — academic, social, behavioral, communication…" data-testid="iep-needs" />
            </div>
            <div className="space-y-2">
              <Label>Parent concerns (optional)</Label>
              <Textarea rows={2} value={inputs.parent_concerns} onChange={(e) => setInputs({ ...inputs, parent_concerns: e.target.value })} placeholder="What does the family hope to see?" data-testid="iep-parent" />
            </div>
            <div className="space-y-2">
              <Label>Assessment summary (optional)</Label>
              <Textarea rows={2} value={inputs.assessment_summary} onChange={(e) => setInputs({ ...inputs, assessment_summary: e.target.value })} placeholder="Recent test results, observations, or benchmarks." data-testid="iep-assessment" />
            </div>
            <div className="space-y-2">
              <Label>Focus areas for goals</Label>
              <div className="flex flex-wrap gap-2">
                {["Reading", "Writing", "Math", "Communication", "Social/Emotional", "Behavior", "Functional Skills"].map((a) => {
                  const active = inputs.focus_areas.includes(a);
                  return (
                    <button
                      key={a}
                      type="button"
                      onClick={() => toggleFocus(a)}
                      data-testid={`focus-${a}`}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-all ${active ? "bg-[#6E5AA0] text-white border-[#6E5AA0]" : "bg-white text-[#2D322F] border-[#E8E9E6] hover:bg-[#EFEAF5]"}`}
                    >
                      {a}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-8">
            <Button onClick={() => setStep(1)} className="bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid="iep-step-next-1">
              Continue <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        </Card>
      )}

      {step === 1 && (
        <Card className="p-8 sm:p-10 border border-[#E8E9E6] bg-white text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#EFEAF5] flex items-center justify-center mx-auto mb-4">
            <Sparkles className="text-[#6E5AA0]" size={22} />
          </div>
          <h3 className="font-heading text-2xl font-medium ink">Ready to draft the IEP</h3>
          <p className="ink-muted mt-2 max-w-md mx-auto">Claude Sonnet will create a structured draft with present levels, SMART goals, accommodations, and services. You'll review everything before saving.</p>
          {loading ? (
            <div className="mt-8 calm-pulse text-[#6E5AA0] font-medium">Drafting your IEP… this can take 20–40 seconds.</div>
          ) : (
            <div className="mt-8 flex items-center justify-center gap-3">
              <Button variant="ghost" onClick={() => setStep(0)} data-testid="iep-back-0">
                <ArrowLeft size={16} className="mr-2" /> Back
              </Button>
              <Button onClick={generate} className="bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid="iep-generate-button">
                <Sparkles size={16} className="mr-2" /> Generate draft with AI
              </Button>
            </div>
          )}
        </Card>
      )}

      {step === 2 && draft && (
        <div className="space-y-5" data-testid="iep-review">
          <Card className="p-6 sm:p-8 border border-[#E8E9E6] bg-white">
            <Label>Present Levels (PLAAFP)</Label>
            <Textarea
              rows={5}
              className="mt-2"
              value={draft.present_levels || ""}
              onChange={(e) => updateDraftField("present_levels", e.target.value)}
              data-testid="iep-present-levels"
            />
          </Card>

          <Card className="p-6 sm:p-8 border border-[#E8E9E6] bg-white">
            <div className="flex items-center justify-between">
              <h4 className="font-heading text-xl font-medium ink">Annual Goals</h4>
              <Button variant="ghost" size="sm" onClick={addGoal} className="text-[#6E5AA0]" data-testid="iep-add-goal">
                <Plus size={14} className="mr-1" /> Add goal
              </Button>
            </div>
            <div className="mt-4 space-y-4">
              {(draft.goals || []).map((g, idx) => (
                <div key={idx} className="border border-[#EDEDE9] rounded-xl p-4 bg-[#FBFBF9]" data-testid={`iep-goal-${idx}`}>
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="outline" className="bg-white">{g.area || `Goal ${idx + 1}`}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => removeGoal(idx)} className="text-[#BA6356]" data-testid={`iep-remove-goal-${idx}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Area</Label>
                      <Input value={g.area || ""} onChange={(e) => updateGoal(idx, "area", e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Baseline</Label>
                      <Input value={g.baseline || ""} onChange={(e) => updateGoal(idx, "baseline", e.target.value)} />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label className="text-xs">Goal description</Label>
                      <Textarea rows={2} value={g.description || ""} onChange={(e) => updateGoal(idx, "description", e.target.value)} />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label className="text-xs">Target criteria</Label>
                      <Input value={g.target_criteria || ""} onChange={(e) => updateGoal(idx, "target_criteria", e.target.value)} />
                    </div>
                    {(g.short_term_objectives || []).length > 0 && (
                      <div className="sm:col-span-2 text-xs ink-muted">
                        <span className="font-semibold">Objectives:</span> {(g.short_term_objectives || []).join(" • ")}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {["accommodations", "modifications", "services"].map((field) => (
            <Card key={field} className="p-6 sm:p-8 border border-[#E8E9E6] bg-white">
              <div className="flex items-center justify-between">
                <h4 className="font-heading text-xl font-medium ink capitalize">{field}</h4>
                <Button variant="ghost" size="sm" onClick={() => addToList(field)} className="text-[#6E5AA0]" data-testid={`iep-add-${field}`}>
                  <Plus size={14} className="mr-1" /> Add
                </Button>
              </div>
              <div className="mt-4 space-y-2">
                {(draft[field] || []).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input value={item} onChange={(e) => updateList(field, idx, e.target.value)} data-testid={`iep-${field}-${idx}`} />
                    <Button variant="ghost" size="sm" onClick={() => removeFromList(field, idx)} className="text-[#BA6356]">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          ))}

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(1)} data-testid="iep-back-1">
              <ArrowLeft size={16} className="mr-2" /> Regenerate
            </Button>
            <Button onClick={save} disabled={saving} className="bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid="iep-save-button">
              <CheckCircle2 size={16} className="mr-2" /> {saving ? "Saving…" : "Save IEP"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
